import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, type DashboardResponse } from "../lib/api";
import { daysAgo, fmtDuration, fmtHours, fmtKg, fmtKcal, fmtKm, fmtNumber, fmtPace, fmtTimeShort } from "../lib/format";
import { PageHeader } from "../components/EmptyState";

export default function Dashboard() {
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api
      .dashboard()
      .then(setData)
      .catch((e: Error) => setErr(e.message));
  }, []);

  if (err) return <div className="text-loss">Error: {err}</div>;
  if (!data) return <div className="text-ink-subtle">Loading…</div>;

  const goalCal = data.goal?.daily_calorie_target ?? null;
  const consumed = data.food_today?.calories ?? 0;
  const burned = data.exercise_today?.calories_burned ?? 0;
  const net = consumed - burned;
  const pct = goalCal ? Math.min(100, Math.round((consumed / goalCal) * 100)) : null;

  return (
    <div className="mx-auto max-w-2xl animate-fade-in">
      <PageHeader
        title="Today"
        eyebrow={
          <>
            <b>{data.today}</b> · {data.timezone}
          </>
        }
      />
      <div className="space-y-4">
      <Card>
        <Label>Today so far</Label>
        <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Stat
            label="Steps"
            value={fmtNumber(data.health?.steps ?? null, 0)}
            hint={data.prior_7d?.avg_steps != null ? `7-day avg ${fmtNumber(data.prior_7d.avg_steps, 0)}` : undefined}
          />
          <Stat
            label="Resting HR"
            value={data.health?.resting_hr != null ? `${data.health.resting_hr} bpm` : "—"}
            hint={data.prior_7d?.avg_resting_hr != null ? `7-day avg ${Math.round(data.prior_7d.avg_resting_hr)}` : undefined}
          />
          <Stat
            label="Sleep"
            value={fmtHours(data.health?.sleep_duration_min ?? null)}
            hint={data.prior_7d?.avg_sleep_min != null ? `7-day avg ${fmtHours(data.prior_7d.avg_sleep_min)}` : "No sleep recorded"}
          />
          <Stat label="Active" value={data.health?.active_minutes != null ? `${data.health.active_minutes} min` : "—"} />
        </div>
      </Card>

      <Card>
        <Label>Training</Label>
        {data.last_workout ? (
          <>
            <Big>
              {data.last_workout.distance_km ? fmtKm(data.last_workout.distance_km) : fmtDuration(data.last_workout.duration_min)}{" "}
              <span className="font-sans text-base font-normal capitalize text-ink-muted">{data.last_workout.activity_type}</span>
            </Big>
            <Sub>
              {fmtTimeShort(data.last_workout.started_at)} · {fmtDuration(data.last_workout.duration_min)}
              {data.last_workout.distance_km ? ` · ${fmtPace({ mps: data.last_workout.avg_speed_mps, km: data.last_workout.distance_km, min: data.last_workout.duration_min })}` : ""}
              {data.last_workout.avg_hr != null ? ` · ♥ ${data.last_workout.avg_hr} avg` : ""}
            </Sub>
          </>
        ) : (
          <Sub>No workouts recorded yet.</Sub>
        )}
        <div className="mt-3 grid grid-cols-3 gap-2">
          <Stat label="Runs · 7d" value={fmtNumber(data.week?.runs ?? 0)} />
          <Stat label="Run km · 7d" value={fmtKm(data.week?.run_km ?? 0)} />
          <Stat label="Time · 7d" value={fmtDuration(data.week?.duration_min ?? 0)} />
        </div>
        <Link to="/activity" className="btn-secondary mt-3">
          See workouts
        </Link>
      </Card>

      <Card>
        <Label>Weight</Label>
        <Big>{fmtKg(data.latest_weight?.weight_kg ?? null)}</Big>
        <Sub>
          {data.latest_weight?.recorded_at
            ? `Last weighed ${fmtTimeShort(data.latest_weight.recorded_at)}${(daysAgo(data.latest_weight.recorded_at) ?? 0) >= 2 ? ` · ${daysAgo(data.latest_weight.recorded_at)} days ago` : ""}`
            : "No weigh-ins yet"}
          {data.latest_weight?.source ? ` · ${data.latest_weight.source}` : ""}
        </Sub>
        {data.scale_last_sync && (daysAgo(data.scale_last_sync) ?? 0) >= 3 && (
          <div className="mt-3 rounded-md border border-loss/30 bg-loss/5 px-3 py-2 text-xs text-loss">
            The scale last reached Withings {fmtTimeShort(data.scale_last_sync)} ({daysAgo(data.scale_last_sync)} days
            ago). Weigh-ins stay on the scale until it reconnects: open the Withings app, then
            Devices → Body Smart → Wi-Fi settings.
          </div>
        )}
        <Link to="/weight" className="btn-accent mt-3">
          Log weight
        </Link>
      </Card>

      <Card>
        <Label>Calories today</Label>
        <Big>{fmtKcal(consumed)}</Big>
        <Sub>
          {goalCal ? `${pct}% of ${fmtKcal(goalCal)} target` : "Set a goal to see progress"} ·{" "}
          net {fmtKcal(net)}
        </Sub>
        {goalCal && (
          <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-ink-line">
            <div
              className="h-2 rounded-full bg-accent transition-all duration-slow"
              style={{ width: `${pct ?? 0}%` }}
            />
          </div>
        )}
        <div className="mt-3 grid grid-cols-3 gap-2">
          <Stat label="Protein" value={`${fmtNumber(data.food_today?.protein, 0)}g`} />
          <Stat label="Carbs" value={`${fmtNumber(data.food_today?.carbs, 0)}g`} />
          <Stat label="Fat" value={`${fmtNumber(data.food_today?.fat, 0)}g`} />
        </div>
      </Card>

      <Card>
        <Label>Recent food</Label>
        {data.recent_food.length === 0 ? (
          <Sub>Nothing logged yet.</Sub>
        ) : (
          <ul className="mt-2 divide-y divide-ink-line-soft">
            {data.recent_food.map((f) => (
              <li key={f.id} className="flex items-baseline justify-between py-2.5">
                <div className="min-w-0">
                  <div className="truncate text-sm text-ink">{f.description}</div>
                  <div className="mt-0.5 text-xs text-ink-subtle">
                    <span className="capitalize">{f.meal_type ?? "—"}</span> ·{" "}
                    {fmtTimeShort(f.logged_at)}
                    {f.confirmed === 0 ? (
                      <span className="pill-warn ml-1.5">pending</span>
                    ) : null}
                  </div>
                </div>
                <div className="amount-spend ml-3 shrink-0 text-sm">
                  {fmtKcal(f.calories_kcal)}
                </div>
              </li>
            ))}
          </ul>
        )}
        <Link to="/food" className="btn-secondary mt-3">
          Log food
        </Link>
      </Card>
      </div>
    </div>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return <div className="card-pad">{children}</div>;
}
function Label({ children }: { children: React.ReactNode }) {
  return <div className="caption">{children}</div>;
}
function Big({ children }: { children: React.ReactNode }) {
  return <div className="amount-display mt-1.5 text-display leading-none">{children}</div>;
}
function Sub({ children }: { children: React.ReactNode }) {
  return <div className="mt-2 text-xs text-ink-muted">{children}</div>;
}
function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-md border border-ink-line bg-canvas px-2 py-1.5 text-center">
      <div className="text-[10px] uppercase tracking-wider text-ink-subtle">{label}</div>
      <div className="mt-0.5 text-sm font-medium tabular-nums text-ink">{value}</div>
      {hint && <div className="mt-0.5 text-[10px] text-ink-subtle">{hint}</div>}
    </div>
  );
}
